/**
 * threadGroup — collapse a flat ``InboxLiveItem[]`` stream into one row
 * per email/chat thread, preserving the BE order of the most-recent
 * message in each thread.
 *
 * The wire shape already carries :prop:`InboxLiveItem.thread_id` (Gmail
 * threadId / Outlook conversationId / PEC native id / chat
 * conversation_id), so we don't need FE subject-normalisation
 * heuristics: same ``thread_id`` ⇒ same thread, end of story. Rows
 * without a ``thread_id`` are bucketed as singletons (``message_id`` as
 * the synthetic thread key) so they keep behaving like their old self.
 *
 * The output preserves the order of the FIRST occurrence per thread in
 * the input — typically newest-first, since :func:`useInboxLiveQuery`
 * flattens pages in BE order. The thread's representative row is the
 * newest message (heuristically, the first occurrence in newest-first
 * input) so the subject/preview shown is what the user expects.
 *
 * Aggregates exposed per group:
 *   - ``count``                — total messages in the thread
 *   - ``unread_count``         — how many of them have ``is_read=false``
 *   - ``has_unread``           — convenience boolean for the row badge
 *   - ``has_attachments_any``  — any sibling carries attachments
 *   - ``is_starred_any``       — any sibling is starred (provider truth)
 *   - ``newest``               — the representative row (first-seen)
 *   - ``message_ids``          — every sibling id, oldest-last in BE order
 *
 * Toggle
 * ------
 * The route exposes a ``?group=thread|flat`` URL toggle paired with the
 * recency/source toggle pattern from documents. ``flat`` returns the
 * input verbatim; ``thread`` collapses by :prop:`thread_id`.
 */

import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";

import type { InboxLiveItem } from "@ando/core";


export type InboxGroupingMode = "thread" | "flat";

export const INBOX_GROUPING_MODES: ReadonlyArray<InboxGroupingMode> = [
  "thread",
  "flat",
];

export const INBOX_GROUPING_LABELS: Readonly<
  Record<InboxGroupingMode, MessageDescriptor>
> = {
  thread: msg`Per thread`,
  flat: msg`Per messaggio`,
};

export const DEFAULT_INBOX_GROUPING_MODE: InboxGroupingMode = "thread";


export function parseInboxGroupingMode(
  raw: string | undefined | null,
): InboxGroupingMode {
  return raw === "flat" || raw === "thread" ? raw : DEFAULT_INBOX_GROUPING_MODE;
}

export function encodeInboxGroupingMode(
  mode: InboxGroupingMode,
): string | undefined {
  return mode === DEFAULT_INBOX_GROUPING_MODE ? undefined : mode;
}


export interface InboxThreadGroup {
  /** Stable key used for ``React.key`` + selection bookkeeping. */
  readonly thread_id: string;
  /** Newest row in the thread — drives the subject/preview render. */
  readonly newest: InboxLiveItem;
  /** Total sibling count (≥1). */
  readonly count: number;
  /** Sibling rows where the provider says ``is_read === false``. */
  readonly unread_count: number;
  readonly has_unread: boolean;
  readonly has_attachments_any: boolean;
  readonly is_starred_any: boolean;
  /** Oldest-last sibling ids — drives bulk operations across the thread. */
  readonly message_ids: ReadonlyArray<string>;
}


/**
 * Resolve a stable thread key — falls back to ``message_id`` for rows
 * the BE didn't populate (legacy pre-thread feed). Trimmed because the
 * Outlook live adapter occasionally emits trailing whitespace on the
 * conversationId.
 */
function threadKey(item: InboxLiveItem): string {
  const t = item.thread_id?.trim();
  return t && t.length > 0 ? t : item.message_id;
}


/**
 * Group items by thread. Preserves the input order (assumed newest-first
 * by the BE) — the first row encountered for a thread becomes
 * :prop:`newest`, and the group lands in the output at that position.
 *
 * Empty input → empty output. Items with the same ``thread_id`` but
 * different providers/accounts get bucketed together (the BE never
 * generates the same thread id across providers, so this is safe).
 */
export function groupByThread(
  items: readonly InboxLiveItem[],
): readonly InboxThreadGroup[] {
  if (items.length === 0) return [];
  // Two-pass: first pass establishes order via Map insertion, second
  // pass aggregates. Single-pass would force a fragile ``unshift`` to
  // preserve "first seen wins newest" — much cleaner to keep both apart.
  const buckets = new Map<string, InboxLiveItem[]>();
  for (const item of items) {
    const key = threadKey(item);
    const list = buckets.get(key);
    if (list) list.push(item);
    else buckets.set(key, [item]);
  }
  const out: InboxThreadGroup[] = [];
  for (const [thread_id, siblings] of buckets) {
    const newest = siblings[0];
    if (!newest) continue;
    let unread = 0;
    let hasAttachments = false;
    let isStarred = false;
    for (const s of siblings) {
      if (!s.is_read) unread += 1;
      if (s.has_attachments) hasAttachments = true;
      if (s.is_starred) isStarred = true;
    }
    out.push({
      thread_id,
      newest,
      count: siblings.length,
      unread_count: unread,
      has_unread: unread > 0,
      has_attachments_any: hasAttachments,
      is_starred_any: isStarred,
      // Oldest-last sibling ids — reverse the BE newest-first ordering so
      // downstream "open thread" navigation reads naturally.
      message_ids: siblings.map((s) => s.message_id).slice().reverse(),
    });
  }
  return out;
}
